import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useInboxCounts } from '@/context/InboxContext';
import GlobalPatientSearch from '@/components/GlobalPatientSearch';
import AccountMenu, { type ShellUser } from './AccountMenu';
import BrandMark from './BrandMark';
import NavIcon from './NavIcon';
import OpenChartTabs from './OpenChartTabs';
import { NAV_ITEMS, activeNavId, type NavItem } from './navItems';
import useOpenCharts from './useOpenCharts';
import useShellChrome from './useShellChrome';

export interface AppShellProps {
  user: ShellUser | null;
  role: string;
  avatarURL: string;
  children: ReactNode;
}

function Badge({ count }: { count: number }) {
  if (!count) return null;
  return (
    <span className="min-w-[18px] rounded-full bg-primary-600 px-1.5 text-center text-[11px] font-semibold leading-[17px] text-white">
      {count > 99 ? '99+' : count}
    </span>
  );
}

/**
 * App chrome. Shell A ("bar", default): navigation, open charts, search and
 * account in one 48 px top bar. Shell B ("rail", a per-user preference on the
 * Account page): navigation in a 64 px left rail and open charts in a 40 px
 * strip. Below lg everyone gets the bar; on phones navigation moves to a
 * bottom tab bar. Matches the "App shell" boards on the design canvas.
 */
export default function AppShell({ user, role, avatarURL, children }: AppShellProps) {
  const { layout, isPhone, topPx } = useShellChrome();

  // Pages that size to the viewport (PACS, side panels) subtract this.
  useEffect(() => {
    document.documentElement.style.setProperty('--app-chrome', `${topPx}px`);
  }, [topPx]);
  const { counts } = useInboxCounts();
  const location = useLocation();
  const { charts, open, close } = useOpenCharts(true);
  const active = activeNavId(location.pathname);
  const inbox = counts?.total ?? 0;

  if (layout === 'rail') {
    return (
      <>
        <nav aria-label="Primary" className="fixed inset-y-0 left-0 z-50 flex w-16 flex-col items-center gap-1 border-r border-slate-200 bg-white py-2.5 dark:border-slate-800 dark:bg-slate-900">
          <div className="mb-2"><BrandMark compact /></div>
          {NAV_ITEMS.map((n) => (
            <RailLink key={n.id} item={n} active={active === n.id} badge={n.inboxBadge ? inbox : 0} />
          ))}
          <span className="flex-1" />
          <AccountMenu user={user} role={role} avatarURL={avatarURL} placement="right" />
        </nav>
        <div className="pl-16">
          <header className="sticky top-0 z-50 flex h-10 items-stretch gap-3 border-b border-slate-200 bg-slate-50 pr-3 dark:border-slate-800 dark:bg-slate-950">
            {charts.length ? (
              <OpenChartTabs charts={charts} onOpen={open} onClose={close} variant="tab" />
            ) : (
              <span className="flex flex-1 items-center pl-4 text-[13px] text-slate-600 dark:text-slate-300">No charts open</span>
            )}
            <div className="flex w-60 flex-none items-center"><GlobalPatientSearch compact /></div>
          </header>
          {children}
        </div>
      </>
    );
  }

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <div className="flex h-12 items-center gap-3 px-3 md:gap-3.5 md:px-4">
          {isPhone ? (
            <PhoneBar charts={charts} onOpen={open} onClose={close} user={user} role={role} avatarURL={avatarURL} />
          ) : (
            <>
              <BrandMark />
              <nav aria-label="Primary" className="flex h-12 flex-none items-stretch">
                {NAV_ITEMS.map((n) => (
                  <Link
                    key={n.id}
                    to={n.path}
                    aria-current={active === n.id ? 'page' : undefined}
                    aria-label={n.label}
                    className={`flex items-center gap-1.5 whitespace-nowrap px-2.5 text-[13.5px] ${active === n.id
                      ? 'font-semibold text-slate-900 shadow-[inset_0_-2px_0_#2563eb] dark:text-slate-50 dark:shadow-[inset_0_-2px_0_#60a5fa]'
                      : 'font-medium text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-50'}`}
                  >
                    <NavIcon path={n.icon} size={16} className="lg:hidden" />
                    <span className="hidden lg:inline">{n.label}</span>
                    {n.inboxBadge && <Badge count={inbox} />}
                  </Link>
                ))}
              </nav>
              <span aria-hidden="true" className="h-6 w-px flex-none bg-slate-200 dark:bg-slate-700" />
              <div className="flex min-w-0 flex-1">
                <OpenChartTabs charts={charts} onOpen={open} onClose={close} variant="chip" />
              </div>
              <div className="w-44 flex-none xl:w-56"><GlobalPatientSearch compact /></div>
              <AccountMenu user={user} role={role} avatarURL={avatarURL} />
            </>
          )}
        </div>
      </header>
      <div className={isPhone ? 'pb-16' : ''}>{children}</div>
      {isPhone && <PhoneTabBar active={active} inbox={inbox} />}
    </>
  );
}

function RailLink({ item, active, badge }: { item: NavItem; active: boolean; badge: number }) {
  return (
    <Link
      to={item.path}
      aria-current={active ? 'page' : undefined}
      className={`flex h-[52px] w-14 flex-col items-center justify-center gap-0.5 rounded-lg text-[10.5px] ${active
        ? 'bg-primary-50 font-semibold text-primary-700 dark:bg-primary-900/40 dark:text-primary-200'
        : 'font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-50'}`}
    >
      <span className="relative flex">
        <NavIcon path={item.icon} />
        {badge > 0 && (
          <span className="absolute -right-2.5 -top-1.5 rounded-full bg-primary-600 px-1 text-[10px] font-semibold leading-[15px] text-white">{badge > 99 ? '99+' : badge}</span>
        )}
      </span>
      {item.label}
    </Link>
  );
}

/** Phone top bar: open-chart switcher, search toggle, account. */
function PhoneBar({ charts, onOpen, onClose, user, role, avatarURL }: {
  charts: ReturnType<typeof useOpenCharts>['charts'];
  onOpen: (id: string) => void;
  onClose: (id: string) => void;
  user: ShellUser | null;
  role: string;
  avatarURL: string;
}) {
  const [menu, setMenu] = useState(false);
  const [searching, setSearching] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = charts.find((c) => c.active);

  useEffect(() => {
    if (!menu) return;
    const onDown = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) setMenu(false); };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [menu]);

  if (searching) {
    return (
      <>
        <div className="min-w-0 flex-1"><GlobalPatientSearch compact /></div>
        <button type="button" onClick={() => setSearching(false)} className="h-10 flex-none px-2 text-sm font-medium text-primary-700 dark:text-primary-300">Cancel</button>
      </>
    );
  }

  return (
    <>
      <BrandMark compact />
      <div ref={ref} className="relative min-w-0 flex-1">
        {charts.length > 0 ? (
          <button
            type="button"
            onClick={() => setMenu((m) => !m)}
            aria-haspopup="menu"
            aria-expanded={menu}
            aria-label={`Open charts, ${charts.length} open${current ? `. Showing ${current.label}` : ''}`}
            className="flex h-9 w-full min-w-0 items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-2.5 text-[13.5px] text-slate-900 dark:border-primary-800 dark:bg-primary-900/40 dark:text-slate-50"
          >
            <span className="truncate font-semibold">{current?.label ?? 'Open charts'}</span>
            {current?.meta && <span className="text-xs text-slate-600 dark:text-slate-300">{current.meta}</span>}
            <span className="flex-1" />
            <span className="rounded-full border border-primary-200 bg-white px-1.5 text-[11px] text-slate-700 dark:border-primary-800 dark:bg-slate-900 dark:text-slate-200">{charts.length} open</span>
          </button>
        ) : (
          <span className="text-[15px] font-semibold">Chartly</span>
        )}
        {menu && (
          <div role="menu" className="absolute left-0 right-0 top-11 z-[60] rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg dark:border-slate-700 dark:bg-slate-900">
            {charts.map((c) => (
              <div key={c.id} className="flex items-center gap-1">
                <button type="button" role="menuitem" onClick={() => { setMenu(false); onOpen(c.id); }} className={`flex min-h-[44px] flex-1 items-center gap-2 rounded-md px-2.5 text-left text-sm ${c.active ? 'font-semibold' : ''} hover:bg-slate-100 dark:hover:bg-slate-800`}>
                  {c.status && <span aria-hidden="true" className={`h-2 w-2 rounded-full ${c.status.dot}`} />}
                  {c.label} <span className="text-xs text-slate-600 dark:text-slate-300">{c.meta}</span>
                </button>
                <button type="button" onClick={() => onClose(c.id)} aria-label={`Close chart ${c.label}`} className="flex h-11 w-11 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <button type="button" onClick={() => setSearching(true)} aria-label="Find patient" className="flex h-10 w-10 flex-none items-center justify-center rounded-lg text-slate-800 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-800">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
      </button>
      <AccountMenu user={user} role={role} avatarURL={avatarURL} />
    </>
  );
}

/** Phone bottom navigation: the three daily destinations + More. */
function PhoneTabBar({ active, inbox }: { active: NavItem['id'] | null; inbox: number }) {
  const [more, setMore] = useState(false);
  const main = NAV_ITEMS.filter((n) => n.id === 'schedule' || n.id === 'patients' || n.id === 'inbox');
  const rest = NAV_ITEMS.filter((n) => !main.includes(n));
  const tab = (on: boolean) => `flex flex-col items-center justify-center gap-0.5 text-xs ${on ? 'font-semibold text-primary-700 dark:text-primary-300' : 'font-medium text-slate-600 dark:text-slate-300'}`;
  return (
    <nav aria-label="Primary" className="fixed inset-x-0 bottom-0 z-50 grid h-16 grid-cols-4 border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom)] dark:border-slate-800 dark:bg-slate-900">
      {main.map((n) => (
        <Link key={n.id} to={n.path} aria-current={active === n.id ? 'page' : undefined} className={tab(active === n.id)} onClick={() => setMore(false)}>
          <span className="relative flex"><NavIcon path={n.icon} />{n.inboxBadge && inbox > 0 && <span className="absolute -right-3 -top-1.5 rounded-full bg-primary-600 px-1 text-[10px] font-semibold leading-[15px] text-white">{inbox > 99 ? '99+' : inbox}</span>}</span>
          {n.label}
        </Link>
      ))}
      <div className="relative flex">
        <button type="button" onClick={() => setMore((m) => !m)} aria-expanded={more} className={`${tab(rest.some((n) => n.id === active))} w-full`}>
          <NavIcon path="M5 12h.01M12 12h.01M19 12h.01" />
          More
        </button>
        {more && (
          <div role="menu" className="absolute bottom-[68px] right-2 w-48 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg dark:border-slate-700 dark:bg-slate-900">
            {rest.map((n) => (
              <Link key={n.id} to={n.path} role="menuitem" onClick={() => setMore(false)} className="flex min-h-[44px] items-center gap-2.5 rounded-md px-2.5 text-sm text-slate-800 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-800">
                <NavIcon path={n.icon} size={18} />{n.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
}
